import React, { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Pencil, Target as TargetIcon } from 'lucide-react'
import { Card, CardContent } from '../../../components/ui/card'
import { Button } from '../../../components/ui/button'
import { cn } from '../../../lib/utils'
import { MacroTargetsEditor } from './MacroTargetsEditor'
import { useTheme } from '../../../contexts/ThemeContext'

// Small circular progress ring rendered with SVG. Caps progress at 100%
// visually but shows the raw percentage when over (e.g. 120%).
function Ring({ progress, size = 56, stroke = 5, color, label, valueLine }) {
  const r = (size - stroke) / 2
  const circumference = 2 * Math.PI * r
  const pct = Math.max(0, progress)
  const visiblePct = Math.min(1, pct)
  const offset = circumference * (1 - visiblePct)
  const overTarget = pct > 1
  return (
    <div className="flex flex-col items-center">
      <div className="relative" style={{ width: size, height: size }}>
        <svg width={size} height={size} aria-hidden="true">
          <circle
            cx={size / 2}
            cy={size / 2}
            r={r}
            fill="none"
            stroke="rgb(148 163 184 / 0.25)"
            strokeWidth={stroke}
          />
          <circle
            cx={size / 2}
            cy={size / 2}
            r={r}
            fill="none"
            stroke={overTarget ? '#ef4444' : color}
            strokeWidth={stroke}
            strokeLinecap="round"
            strokeDasharray={circumference}
            strokeDashoffset={offset}
            transform={`rotate(-90 ${size / 2} ${size / 2})`}
            style={{ transition: 'stroke-dashoffset 0.6s ease-out' }}
          />
        </svg>
        <div className="absolute inset-0 flex items-center justify-center">
          <span className={cn(
            'text-[11px] font-mono font-bold',
            overTarget ? 'text-red-500' : 'text-foreground'
          )}>
            {Math.round(pct * 100)}%
          </span>
        </div>
      </div>
      <p className="text-[10px] uppercase tracking-wide text-muted-foreground mt-1.5 font-medium">
        {label}
      </p>
      <p className="text-[11px] font-mono text-foreground" dir="ltr">
        {valueLine}
      </p>
    </div>
  )
}

export function MacroRings({ meals, profile, onSaveTargets, onClearTargets }) {
  const { t } = useTranslation()
  const { primaryHsl } = useTheme()
  const primary500 = primaryHsl(500)
  const [editing, setEditing] = useState(false)
  const [submitting, setSubmitting] = useState(false)

  const totals = useMemo(() => {
    if (!Array.isArray(meals) || meals.length === 0) {
      return { kcal: 0, p: 0, c: 0, f: 0, hasMacros: false }
    }
    return meals.reduce(
      (acc, m) => ({
        kcal: acc.kcal + (Number(m.kcal) || 0),
        p: acc.p + (Number(m.protein_g) || 0),
        c: acc.c + (Number(m.carbs_g) || 0),
        f: acc.f + (Number(m.fat_g) || 0),
        hasMacros: acc.hasMacros || m.protein_g != null || m.carbs_g != null || m.fat_g != null,
      }),
      { kcal: 0, p: 0, c: 0, f: 0, hasMacros: false }
    )
  }, [meals])

  const targets = {
    kcal: profile?.kcal_target,
    p: profile?.protein_target_g,
    c: profile?.carbs_target_g,
    f: profile?.fat_target_g,
  }
  const hasAnyTarget =
    targets.kcal != null || targets.p != null || targets.c != null || targets.f != null

  const handleSave = async (values) => {
    setSubmitting(true)
    const ok = await onSaveTargets(values)
    setSubmitting(false)
    if (ok) setEditing(false)
  }

  const handleClear = async () => {
    setSubmitting(true)
    await onClearTargets()
    setSubmitting(false)
    setEditing(false)
  }

  // Editor mode (replaces the rings while open) — keeps the layout stable.
  if (editing) {
    return (
      <Card className="border-app-primary-500/30">
        <CardContent className="p-4">
          <MacroTargetsEditor
            initialValues={{
              kcal: targets.kcal ?? '',
              protein: targets.p ?? '',
              carbs: targets.c ?? '',
              fat: targets.f ?? '',
            }}
            submitting={submitting}
            onSave={handleSave}
            onCancel={() => setEditing(false)}
            onClear={hasAnyTarget ? handleClear : null}
          />
        </CardContent>
      </Card>
    )
  }

  // No targets set + no meals logged → simple set-targets CTA card.
  if (!hasAnyTarget && (!meals || meals.length === 0)) {
    return (
      <Card>
        <CardContent className="p-4 text-center">
          <TargetIcon className="w-7 h-7 text-app-primary-500 mx-auto mb-2" />
          <p className="text-sm text-foreground font-medium mb-1">
            {t('phoenixTracker.meals.targets.title')}
          </p>
          <p className="text-xs text-muted-foreground mb-3">
            {t('phoenixTracker.meals.targets.noTargets')}
          </p>
          <Button
            size="sm"
            onClick={() => setEditing(true)}
            className="bg-app-primary-500 hover:bg-app-primary-600 text-white"
          >
            {t('phoenixTracker.meals.targets.set')}
          </Button>
        </CardContent>
      </Card>
    )
  }

  // No targets but meals exist → keep the simple totals card from before,
  // with an inline "Set targets" affordance.
  if (!hasAnyTarget) {
    return (
      <Card className="border-app-primary-500/30">
        <CardContent className="p-4">
          <div className="flex items-center justify-between mb-1">
            <p className="text-[10px] uppercase tracking-wide text-muted-foreground font-medium">
              {t('phoenixTracker.meals.dailyTotal')}
            </p>
            <button
              type="button"
              onClick={() => setEditing(true)}
              className="text-xs text-app-primary-500 hover:text-app-primary-600 font-medium"
            >
              {t('phoenixTracker.meals.targets.set')}
            </button>
          </div>
          <p className="text-3xl font-bold font-mono text-app-primary-500" dir="ltr">
            {Math.round(totals.kcal).toLocaleString()}
            <span className="text-sm font-normal text-muted-foreground ms-1.5">
              {t('phoenixTracker.meals.kcalUnit')}
            </span>
          </p>
          {totals.hasMacros && (
            <p className="text-xs text-muted-foreground mt-1 font-mono" dir="ltr">
              {t('phoenixTracker.meals.macrosShort', {
                protein: Math.round(totals.p),
                carbs: Math.round(totals.c),
                fat: Math.round(totals.f),
              })}
            </p>
          )}
        </CardContent>
      </Card>
    )
  }

  // Targets set → render the rings card.
  const progressOrNull = (consumed, target) =>
    target != null && target > 0 ? consumed / target : null

  const kcalProgress = progressOrNull(totals.kcal, targets.kcal)

  return (
    <Card className="border-app-primary-500/30">
      <CardContent className="p-4">
        <div className="flex items-center justify-between mb-3">
          <p className="text-[10px] uppercase tracking-wide text-muted-foreground font-medium">
            {t('phoenixTracker.meals.targets.title')}
          </p>
          <button
            type="button"
            onClick={() => setEditing(true)}
            aria-label={t('phoenixTracker.meals.targets.edit')}
            className="p-1.5 rounded-md hover:bg-accent text-muted-foreground hover:text-foreground"
          >
            <Pencil className="w-3.5 h-3.5" />
          </button>
        </div>

        {/* KCAL — bigger center ring */}
        {kcalProgress != null && (
          <div className="flex justify-center mb-4">
            <Ring
              progress={kcalProgress}
              size={92}
              stroke={7}
              color={primary500}
              label={t('phoenixTracker.meals.rings.kcal')}
              valueLine={`${Math.round(totals.kcal)} / ${targets.kcal}`}
            />
          </div>
        )}

        {/* Macros — 3 smaller rings */}
        <div className="grid grid-cols-3 gap-2">
          {targets.p != null && (
            <Ring
              progress={progressOrNull(totals.p, targets.p) ?? 0}
              color="#ef4444"
              label={t('phoenixTracker.meals.rings.protein')}
              valueLine={`${Math.round(totals.p)} / ${targets.p}g`}
            />
          )}
          {targets.c != null && (
            <Ring
              progress={progressOrNull(totals.c, targets.c) ?? 0}
              color="#3b82f6"
              label={t('phoenixTracker.meals.rings.carbs')}
              valueLine={`${Math.round(totals.c)} / ${targets.c}g`}
            />
          )}
          {targets.f != null && (
            <Ring
              progress={progressOrNull(totals.f, targets.f) ?? 0}
              color="#eab308"
              label={t('phoenixTracker.meals.rings.fat')}
              valueLine={`${Math.round(totals.f)} / ${targets.f}g`}
            />
          )}
        </div>
      </CardContent>
    </Card>
  )
}
